import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { heroData, latestLinks, features, popularCategories, faqs, stats } from '@/mocks/home';
import { getRecentArticles } from '@/mocks/articles';

export default function Home() {
  const recentArticles = getRecentArticles(6);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '91蜜桃官方导航',
    url: 'https://91mitao.com',
    description: '91蜜桃官方最新地址，提供安全稳定的访问入口与使用指南。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://91mitao.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91蜜桃',
    url: 'https://91mitao.com',
    description: '91蜜桃是国内领先的在线内容平台。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@91mitao.com',
      contactType: 'customer support',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="91蜜桃-最新入口地址与安全访问指南 | 91蜜桃官方导航"
        description="91蜜桃官方最新地址，提供安全稳定的访问入口与使用指南。收录91蜜桃最新链接、镜像地址及常见问题解答，帮助用户快速找到可用入口，安全畅享平台内容。"
        keywords="91蜜桃,91蜜桃入口,91蜜桃最新地址,91蜜桃官网,蜜桃视频,91蜜桃镜像,蜜桃在线"
        canonicalUrl="https://91mitao.com/"
      />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20soft%20peach%20and%20coral%20tones%2C%20elegant%20flowing%20curves%2C%20minimalist%20geometric%20shapes%20in%20warm%20amber%20and%20rose%20gold%20hues%2C%20smooth%20texture%2C%20modern%20clean%20aesthetic%2C%20ethereal%20atmospheric%20light%20with%20gentle%20bokeh%20effect%2C%20luxurious%20and%20sophisticated%20composition&width=1920&height=1080&seq=hero-bg-mitao-2026&orientation=landscape"
              alt="91蜜桃官方导航背景"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
                {heroData.title}
              </h1>
              <p className="text-lg md:text-xl text-white/90 font-medium mb-3">
                {heroData.subtitle}
              </p>
              <p className="text-sm md:text-base text-white/75 max-w-xl mx-auto mb-8 leading-relaxed">
                {heroData.description}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to={heroData.ctaLink}
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-all text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-link-m mr-2"></i>
                  {heroData.ctaText}
                </Link>
                <Link
                  to={heroData.secondaryCtaLink}
                  className="w-full sm:w-auto px-8 py-3 bg-white/15 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/25 transition-all text-sm whitespace-nowrap cursor-pointer border border-white/20"
                >
                  <i className="ri-book-open-line mr-2"></i>
                  {heroData.secondaryCtaText}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="relative -mt-10 z-20">
          <div className="px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-xl py-6 px-6 grid grid-cols-2 md:grid-cols-4 gap-4 border border-background-200/70">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-xl md:text-2xl font-heading font-bold text-primary-600">
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Latest Links */}
        <section className="py-14 md:py-20">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  最新可用入口
                </h2>
                <p className="mt-3 text-sm text-foreground-600 max-w-lg mx-auto">
                  以下入口均已验证可用，建议优先使用标记为「推荐」的主入口进行访问
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {latestLinks.map((link) => (
                  <div
                    key={link.id}
                    className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300/50 transition-all cursor-pointer group"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <i className="ri-link-m text-primary-600 text-sm"></i>
                        </div>
                        <span className="font-semibold text-sm text-foreground-900">
                          {link.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {link.badge && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                            {link.badge}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                          {link.status}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-500 truncate mb-3 font-mono">
                      {link.url}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <i className="ri-speed-line"></i>
                        {link.speed}
                      </span>
                      <span className="text-xs text-primary-600 group-hover:underline whitespace-nowrap">
                        立即访问 <i className="ri-arrow-right-line text-[10px]"></i>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  为什么选择我们
                </h2>
                <p className="mt-3 text-sm text-foreground-600 max-w-lg mx-auto">
                  我们致力于为每一位用户提供安全、稳定、便捷的访问服务
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {features.map((feature) => (
                  <div key={feature.title} className="text-center p-5">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                      <i className={`${feature.icon} text-xl text-primary-600`}></i>
                    </div>
                    <h3 className="font-semibold text-sm text-foreground-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Popular Categories */}
        <section className="py-14 md:py-20">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  内容分类导航
                </h2>
                <p className="mt-3 text-sm text-foreground-600 max-w-lg mx-auto">
                  按分类浏览您感兴趣的内容，快速找到所需信息
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {popularCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300/50 transition-all cursor-pointer group flex items-start gap-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {cat.name}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                        {cat.description}
                      </p>
                      <span className="inline-block mt-2 text-xs text-foreground-400">
                        {cat.articleCount} 篇文章
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Recent Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    最新文章
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    最新的使用教程、常见问题解答与平台动态
                  </p>
                </div>
                <Link
                  to="/category/latest-links"
                  className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {recentArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300/50 transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                        {article.categoryName}
                      </span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                    <h3 className="font-semibold text-sm text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-8 text-center sm:hidden">
                <Link
                  to="/category/latest-links"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 font-medium whitespace-nowrap cursor-pointer"
                >
                  查看全部文章 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-14 md:py-20">
          <div className="px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  常见问题解答
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  以下是用户最常咨询的问题，希望能帮助您更快解决问题
                </p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-900 pr-4">
                        {faq.question}
                      </span>
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background-100 flex items-center justify-center group-open:rotate-45 transition-transform">
                        <i className="ri-add-line text-xs text-foreground-500"></i>
                      </span>
                    </summary>
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                找不到可用入口？
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                收藏本页面，随时获取91蜜桃最新入口地址与使用指南
              </p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/latest-links"
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-all text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-link-m mr-2"></i>
                  查看最新入口
                </Link>
                <Link
                  to="/about"
                  className="w-full sm:w-auto px-8 py-3 border border-background-300 text-foreground-700 font-semibold rounded-lg hover:bg-background-50 transition-all text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-information-line mr-2"></i>
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}